import * as THREE from "three";
import type { AmbientCritterDef, MapData } from "@dogdell/shared";
import { MOVE_SPEED_TILES_PER_SEC, RUN_SPEED_MULT, Tile, tileAt } from "@dogdell/shared";
import { critterSprite } from "./CharacterSprites";
import { makeContactShadow } from "./ContactShadow";
import { billboardMaterial, faceCameraYaw, facingFlipSign, SPRITE_FOOT_Y } from "./WorldArt";

export type DogPose = { x: number; y: number };

type CritterMode = "idle" | "wander" | "flee" | "fly" | "hide";

/** Fastest a pup can sprint - land critters must outrun this when fleeing. */
const DOG_RUN_SPEED = MOVE_SPEED_TILES_PER_SEC * RUN_SPEED_MULT;

/**
 * Small park wildlife as camera-facing sprites (RS-classic living things).
 * Client-authored motion: idle / wander to goals, flee dogs into cover, re-emerge.
 * Pigeons take off into a short flight arc when a pup gets near, then land again.
 */
export class ParkCritter {
  readonly group: THREE.Group;
  private readonly mesh: THREE.Mesh;
  private readonly shadow: THREE.Mesh;
  private readonly spriteH: number;
  private readonly shadowBaseX: number;
  private readonly shadowBaseZ: number;
  private readonly shadowBaseOpacity: number;
  private x: number;
  private y: number;
  private facing = 0;
  private mode: CritterMode = "idle";
  private hideUntil = 0;
  private idleUntil = 0;
  private goalX: number;
  private goalY: number;
  private stuckFrames = 0;
  private readonly roam: number;
  private readonly fleeRadius: number;
  private readonly speedWander: number;
  private readonly speedFlee: number;
  private readonly seed: number;
  private readonly isBird: boolean;
  /** Height above ground while airborne (birds only). */
  private alt = 0;
  private flyVx = 0;
  private flyVz = 0;
  private flyUntil = 0;

  constructor(
    private readonly map: MapData,
    private readonly def: AmbientCritterDef,
    private readonly groundY: (x: number, y: number) => number,
  ) {
    this.x = def.homeX + 0.5;
    this.y = def.homeY + 0.5;
    this.goalX = this.x;
    this.goalY = this.y;
    this.roam = def.roam ?? 6;
    this.isBird = def.kind === "pigeon";
    this.fleeRadius = this.isBird ? 6.2 : def.kind === "duck" ? 4.2 : 5.4;
    this.speedWander =
      def.kind === "pigeon" ? 1.55 : def.kind === "squirrel" ? 2.2 : def.kind === "rabbit" ? 1.9 : 1.45;
    // Squirrels / rabbits must beat a sprinting pup; birds keep a milder burst.
    if (def.kind === "squirrel") {
      this.speedFlee = DOG_RUN_SPEED * 1.22;
    } else if (def.kind === "rabbit") {
      this.speedFlee = DOG_RUN_SPEED * 1.18;
    } else {
      this.speedFlee = this.speedWander * 2.35;
    }
    this.seed = hash(def.id);
    this.idleUntil = performance.now() + 400 + this.seed * 1600;

    const sheet = critterSprite(def.kind);
    this.spriteH = sheet.height;

    this.group = new THREE.Group();
    this.shadow = makeContactShadow({
      radius: Math.min(0.28, sheet.width * 0.34),
      opacity: 0.48,
      y: 0.015,
    });
    // Own material so flight fade doesn't touch other contact shadows.
    this.shadow.material = (this.shadow.material as THREE.MeshBasicMaterial).clone();
    this.shadow.material.userData.shared = false;
    this.shadowBaseOpacity = 0.48;
    this.shadowBaseX = this.shadow.scale.x;
    this.shadowBaseZ = this.shadow.scale.z;
    this.group.add(this.shadow);

    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(sheet.width, sheet.height),
      billboardMaterial(sheet.texture),
    );
    this.mesh.position.y = sheet.height * SPRITE_FOOT_Y;
    this.mesh.renderOrder = 2;
    this.group.add(this.mesh);
    this.place();
  }

  update(dtSec: number, now: number, camera: THREE.Camera, dogs: DogPose[]): void {
    if (this.mode === "hide") {
      if (now >= this.hideUntil && !this.dogTooClose(dogs, this.fleeRadius * 0.85)) {
        this.burstFromCover(now);
      } else {
        this.place();
        return;
      }
    }

    if (this.mode === "fly") {
      this.tickFlight(dtSec, now, dogs);
      this.place();
      this.animateSprite(now, camera, true);
      return;
    }

    const threat = this.nearestDog(dogs);
    if (threat && threat.dist < this.fleeRadius) {
      if (this.isBird) {
        this.beginFlight(threat.x, threat.y, now);
        this.place();
        this.animateSprite(now, camera, true);
        return;
      }
      this.mode = "flee";
      const awayX = this.x - threat.x;
      const awayY = this.y - threat.y;
      const len = Math.hypot(awayX, awayY) || 1;
      this.facing = Math.atan2(awayY, awayX);
      this.step((awayX / len) * this.speedFlee * dtSec, (awayY / len) * this.speedFlee * dtSec, true);
      if (this.tryEnterCover(now)) {
        this.animateSprite(now, camera, true);
        return;
      }
    } else if (this.mode === "flee") {
      // Leave flee without snapping back into a ping-pong leash fight.
      this.beginIdle(now, 500 + this.seed * 900);
    } else if (this.mode === "idle") {
      if (now >= this.idleUntil) {
        this.pickWanderGoal();
        this.mode = "wander";
      }
    } else if (this.mode === "wander") {
      const dx = this.goalX - this.x;
      const dy = this.goalY - this.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 0.35) {
        this.beginIdle(now, 700 + this.seed * 1400);
      } else {
        this.facing = Math.atan2(dy, dx);
        const stepLen = this.speedWander * dtSec;
        const moved = this.step((dx / dist) * stepLen, (dy / dist) * stepLen, false);
        if (!moved) {
          this.stuckFrames += 1;
          if (this.stuckFrames >= 3) {
            this.pickWanderGoal();
            this.stuckFrames = 0;
          }
        } else {
          this.stuckFrames = 0;
        }
      }
    }

    this.place();
    this.animateSprite(now, camera, this.mode === "wander" || this.mode === "flee");
  }

  destroy(): void {
    this.group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (!child.geometry.userData.shared) {
          child.geometry.dispose();
        }
        if (child.material instanceof THREE.Material && !child.material.userData.shared) {
          child.material.dispose();
        }
      }
    });
    this.group.removeFromParent();
  }

  private beginFlight(threatX: number, threatY: number, now: number): void {
    this.mode = "fly";
    const awayX = this.x - threatX;
    const awayY = this.y - threatY;
    const len = Math.hypot(awayX, awayY) || 1;
    const speed = 4.2 + this.seed * 1.6;
    this.flyVx = (awayX / len) * speed + (this.seed - 0.5) * 1.4;
    this.flyVz = (awayY / len) * speed + (hash(this.def.id + "fz") - 0.5) * 1.4;
    this.facing = Math.atan2(this.flyVz, this.flyVx);
    this.alt = Math.max(this.alt, 0.15);
    this.flyUntil = now + 1800 + this.seed * 1400;
    this.mesh.visible = true;
  }

  private tickFlight(dtSec: number, now: number, dogs: DogPose[]): void {
    this.x += this.flyVx * dtSec;
    this.y += this.flyVz * dtSec;
    this.facing = Math.atan2(this.flyVz, this.flyVx);

    const pad = 10;
    this.x = clamp(this.x, -pad, this.map.width + pad);
    this.y = clamp(this.y, -pad, this.map.height + pad);

    const rising = now < this.flyUntil - 700;
    const dogNear = this.dogTooClose(dogs, this.fleeRadius * 1.15);
    if (rising || dogNear) {
      this.alt = Math.min(2.8, this.alt + dtSec * 3.4);
      if (dogNear && now > this.flyUntil - 400) {
        this.flyUntil = now + 900 + this.seed * 600;
      }
    } else {
      this.alt = Math.max(0, this.alt - dtSec * 2.6);
    }

    // Soft bank / wobble while aloft.
    this.flyVx += Math.sin(now * 0.004 + this.seed * 8) * 0.35 * dtSec;
    this.flyVz += Math.cos(now * 0.0035 + this.seed * 6) * 0.35 * dtSec;

    if (this.alt <= 0.04 && now >= this.flyUntil && !dogNear) {
      this.alt = 0;
      this.flyVx = 0;
      this.flyVz = 0;
      // Prefer landing near home on open lawn.
      const hx = this.def.homeX + 0.5;
      const hy = this.def.homeY + 0.5;
      if (Math.hypot(this.x - hx, this.y - hy) > this.roam + 2) {
        this.x = hx + (hash(this.def.id + "lx") - 0.5) * this.roam * 0.8;
        this.y = hy + (hash(this.def.id + "ly") - 0.5) * this.roam * 0.8;
      }
      this.beginIdle(now, 900 + this.seed * 1200);
      this.pickWanderGoal();
    }
  }

  private animateSprite(now: number, camera: THREE.Camera, moving: boolean): void {
    const flying = this.mode === "fly";
    const bob = flying
      ? Math.abs(Math.sin(now * 0.042 + this.seed * 6)) * 0.08
      : moving
        ? Math.abs(Math.sin(now * 0.014 + this.seed * 6)) * 0.025
        : Math.sin(now * 0.004 + this.seed) * 0.005;
    const squash = flying
      ? 1 + Math.sin(now * 0.05 + this.seed) * 0.12
      : moving
        ? 1 + Math.sin(now * 0.014 + this.seed) * 0.03
        : 1;
    this.mesh.position.y = this.spriteH * SPRITE_FOOT_Y + bob;
    faceCameraYaw(this.mesh, camera);
    const flip = facingFlipSign(this.x, this.y, this.facing, camera);
    this.mesh.scale.set(flip * (flying ? squash * 1.08 : squash), squash * (flying ? 0.92 : 1), 1);
  }

  private place(): void {
    const gy = this.groundY(this.x - 0.5, this.y - 0.5);
    const lift = this.mode === "hide" ? 0.55 : this.alt;
    this.group.position.set(this.x, gy + lift, this.y);
    // Shadow stays on the turf under the bird, even while airborne.
    this.shadow.position.y = 0.015 - lift;
    this.shadow.visible = this.mode !== "hide";
    const heightT = Math.min(1, Math.max(0, this.alt / 2.8));
    const shadowScale = this.alt > 0.05 ? Math.max(0.42, 1 - heightT * 0.45) : 1;
    this.shadow.scale.set(this.shadowBaseX * shadowScale, 1, this.shadowBaseZ * shadowScale);
    const mat = this.shadow.material as THREE.MeshBasicMaterial;
    mat.opacity = this.shadowBaseOpacity * (this.alt > 0.05 ? Math.max(0.35, 1 - heightT * 0.5) : 1);
  }

  private beginIdle(now: number, durationMs: number): void {
    this.mode = "idle";
    this.idleUntil = now + durationMs;
    this.stuckFrames = 0;
  }

  private pickWanderGoal(): void {
    const hx = this.def.homeX + 0.5;
    const hy = this.def.homeY + 0.5;
    for (let attempt = 0; attempt < 10; attempt++) {
      const ang = (this.seed * 17.13 + attempt * 2.399 + performance.now() * 0.001) % (Math.PI * 2);
      const radius = 1.2 + ((this.seed * 13 + attempt * 0.37) % 1) * Math.max(1.5, this.roam * 0.85);
      const gx = hx + Math.cos(ang) * radius;
      const gy = hy + Math.sin(ang) * radius;
      if (this.isWalkableOpen(gx, gy)) {
        this.goalX = gx;
        this.goalY = gy;
        return;
      }
    }
    // Fallback: drift toward home.
    this.goalX = hx + (this.seed - 0.5) * 1.4;
    this.goalY = hy + (hash(this.def.id + "g") - 0.5) * 1.4;
  }

  /** Returns false when the step was blocked (so AI can repath). */
  private step(dx: number, dy: number, fleeing: boolean): boolean {
    const nx = this.x + dx;
    const ny = this.y + dy;
    const forestPad = 9.5;
    if (fleeing) {
      this.x = clamp(nx, -forestPad, this.map.width + forestPad);
      this.y = clamp(ny, -forestPad, this.map.height + forestPad);
      return true;
    }

    if (!this.isWalkableOpen(nx, ny)) {
      return false;
    }

    const hx = this.def.homeX + 0.5;
    const hy = this.def.homeY + 0.5;
    if (Math.hypot(nx - hx, ny - hy) > this.roam + 0.6) {
      return false;
    }

    this.x = nx;
    this.y = ny;
    return true;
  }

  private isWalkableOpen(x: number, y: number): boolean {
    const tx = Math.floor(x);
    const ty = Math.floor(y);
    if (tx < 0 || ty < 0 || tx >= this.map.width || ty >= this.map.height) {
      return false;
    }
    const tile = tileAt(this.map, tx, ty);
    if (tile === undefined || tile === Tile.Water || tile === Tile.Rock || tile === Tile.Fence) {
      return false;
    }
    // Trees/bushes are cover when fleeing, not casual stroll tiles.
    if (tile === Tile.Tree || tile === Tile.Bush) {
      return false;
    }
    return true;
  }

  private tryEnterCover(now: number): boolean {
    const tx = Math.floor(this.x);
    const ty = Math.floor(this.y);
    const tile = tileAt(this.map, tx, ty);
    const oob = tx < 0 || ty < 0 || tx >= this.map.width || ty >= this.map.height;
    if (tile === Tile.Tree || tile === Tile.Bush || oob) {
      this.mode = "hide";
      this.hideUntil = now + 2200 + this.seed * 1800;
      this.mesh.visible = false;
      if (oob) {
        const awayX = this.x < 0 ? -1 : this.x >= this.map.width ? 1 : 0;
        const awayY = this.y < 0 ? -1 : this.y >= this.map.height ? 1 : 0;
        this.x += awayX * (1.2 + hash(this.def.id + "hide") * 2.5);
        this.y += awayY * (1.2 + hash(this.def.id + "hidey") * 2.5);
        this.x = clamp(this.x, -9, this.map.width + 9);
        this.y = clamp(this.y, -9, this.map.height + 9);
      }
      return true;
    }
    return false;
  }

  private burstFromCover(now: number): void {
    const hx = this.def.homeX + 0.5;
    const hy = this.def.homeY + 0.5;
    this.x = hx + (hash(this.def.id + "x") - 0.5) * 2;
    this.y = hy + (hash(this.def.id + "y") - 0.5) * 2;
    this.mesh.visible = true;
    this.beginIdle(now, 600 + this.seed * 1000);
    this.pickWanderGoal();
  }

  private nearestDog(dogs: DogPose[]): { x: number; y: number; dist: number } | null {
    let best: { x: number; y: number; dist: number } | null = null;
    for (const dog of dogs) {
      const dx = dog.x + 0.5 - this.x;
      const dy = dog.y + 0.5 - this.y;
      const dist = Math.hypot(dx, dy);
      if (!best || dist < best.dist) {
        best = { x: dog.x + 0.5, y: dog.y + 0.5, dist };
      }
    }
    return best;
  }

  private dogTooClose(dogs: DogPose[], radius: number): boolean {
    const n = this.nearestDog(dogs);
    return Boolean(n && n.dist < radius);
  }
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function hash(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
}
